'use client';

import * as React from 'react';
import {
  useController,
  UseControllerProps,
  FieldValues,
  FieldPath,
  Control,
} from 'react-hook-form';
import { Field, Box } from '@chakra-ui/react';

import { Textarea, TextareaProps } from '../textarea/textarea';

export interface FormTextareaProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<TextareaProps, 'name' | 'defaultValue' | 'value' | 'onChange' | 'onBlur' | 'ref'> {
  /**
   * Field name for React Hook Form
   */
  name: TName;

  /**
   * Control from React Hook Form
   */
  control?: Control<TFieldValues>;

  /**
   * Validation rules for React Hook Form
   */
  rules?: UseControllerProps<TFieldValues, TName>['rules'];

  /**
   * Field label
   */
  label?: string;

  /**
   * Helper text shown below the textarea
   */
  hint?: string;

  /**
   * Whether to show the error message
   * @default true
   */
  showError?: boolean;

  /**
   * Whether the field is required
   */
  required?: boolean;
}

/**
 * Textarea component integrated with React Hook Form
 * Provides consistent Field.Root integration
 */
export function FormTextarea<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  control,
  rules,
  label,
  hint,
  showError = true,
  required,
  disabled,
  className,
  ...props
}: FormTextareaProps<TFieldValues, TName>) {
  const {
    field: { onChange, onBlur, value, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
  });

  const textareaId = React.useId();
  const invalid = !!error;

  return (
    <Field.Root {...(invalid ? { invalid: true } : {})} className={className} disabled={disabled}>
      {/* Label */}
      {label && (
        <Box
          as="label"
          fontSize="sm"
          fontWeight="medium"
          color={invalid ? '#EF4444' : '#374151'}
          {...({ htmlFor: textareaId } as Record<string, unknown>)}
        >
          {label}
          {required && (
            <Box as="span" color="#EF4444" marginLeft={1}>
              *
            </Box>
          )}
        </Box>
      )}

      {/* Textarea */}
      <Textarea
        ref={ref}
        id={textareaId}
        name={name}
        value={value || ''}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
          // Extract value from event for React Hook Form
          const target = e.target as HTMLTextAreaElement;
          onChange(target.value);
        }}
        onBlur={onBlur}
        invalid={invalid}
        disabled={disabled}
        required={required}
        aria-invalid={invalid}
        aria-describedby={invalid ? `${name}-error` : hint ? `${name}-hint` : undefined}
        {...(props as Record<string, unknown>)}
      />

      {/* Error Message */}
      {showError && error && (
        <Box fontSize="sm" color="#EF4444" marginTop={1}>
          {error.message}
        </Box>
      )}

      {/* Hint Message */}
      {hint && !error && (
        <Box fontSize="sm" color="#6B7280" marginTop={1}>
          {hint}
        </Box>
      )}
    </Field.Root>
  );
}

FormTextarea.displayName = 'FormTextarea';
